import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
// The CommonJS build, explicitly: the package's ES build is an .mjs file,
// which this app's webpack 4 (react-scripts 4) can't link against React's
// CommonJS exports ("Can't import the named export 'Component'...").
import Cropper from 'react-easy-crop/index.js';
import { IoRemove, IoAdd } from 'react-icons/io5';
import Modal from './Modal';
import { useLanguage } from '../i18n/LanguageContext';
import '../style/ImageCropModal.css';

// Pick the part of an image to keep before it's uploaded. Only the crop BOX
// is sent (in the image's own pixels, as react-easy-crop reports it); the
// server applies it - to every frame, for a GIF - so the animation survives,
// which a canvas-cropped upload wouldn't.
//
// `file` opens it (null closes it). `aspect` 1 for avatars/icons, 5/2 for
// banners; `round` only changes the guide's shape, the stored image is
// always the full rectangle. `onInvalid` fires when the browser can't open
// the file as an image at all - the cropper would otherwise sit there empty
// with its Save button never enabling.
export default function ImageCropModal({ file, aspect = 1, round = false, title, onCancel, onConfirm, onInvalid }) {
  const { t } = useLanguage();
  const [url, setUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);

  useEffect(() => {
    if (!file) return undefined;
    const objectUrl = URL.createObjectURL(file);
    const probe = new Image();
    probe.onerror = () => { if (onInvalid) onInvalid(); };
    probe.src = objectUrl;
    setUrl(objectUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setPixels(null);
    return () => {
      probe.onerror = null;
      URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const nudgeZoom = (delta) => setZoom((z) => Math.min(4, Math.max(1, Math.round((z + delta) * 10) / 10)));

  // Portalled: Modal centres itself on its nearest positioned ancestor, so
  // opened from inside a settings card it would centre on that card.
  return createPortal(
    <Modal mState={{ iShow: !!file, setiShow: (v) => { if (!v) onCancel(); } }} buttonShow className="ImageCropModalContainer">
      <h4 style={{ marginTop: 0 }}>{title || t('media.cropTitle')}</h4>
      <div className="ImageCropArea" style={{ aspectRatio: aspect >= 1 ? `${Math.min(aspect, 2)} / 1` : '1 / 1' }}>
        {url && (
          <Cropper
            image={url}
            crop={crop}
            zoom={zoom}
            minZoom={1}
            maxZoom={4}
            aspect={aspect}
            cropShape={round ? 'round' : 'rect'}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_area, areaPixels) => setPixels(areaPixels)}
          />
        )}
      </div>
      <div className="ImageCropZoom">
        <button type="button" onClick={() => nudgeZoom(-0.2)} aria-label={t('media.zoomOut')}><IoRemove /></button>
        <input
          type="range"
          min="1"
          max="4"
          step="0.01"
          value={zoom}
          onChange={(e) => setZoom(parseFloat(e.target.value))}
          aria-label={t('media.zoom')}
        />
        <button type="button" onClick={() => nudgeZoom(0.2)} aria-label={t('media.zoomIn')}><IoAdd /></button>
      </div>
      <div className="ConfirmModalActions">
        <button type="button" className="SendButton primaryColor" disabled={!pixels} onClick={() => onConfirm(pixels)}>{t('media.apply')}</button>
        <button type="button" className="SendButton secondaryColor" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </Modal>,
    document.body
  );
}

// Turns an upload result's error code (MediaAPI.uploadImage, the pipeline's
// MediaError codes) into a sentence for the user.
export function mediaErrorText(t, res) {
  const key = res && res.code ? `media.errors.${res.code}` : null;
  const text = key ? t(key) : null;
  return text && text !== key ? text : t('media.errors.generic');
}
